import React, {useCallback, useEffect, useMemo, useState} from 'react';

import {UserLink} from 'src/components/links';
import {ScrollableTable} from 'src/components/scrollable-table';
import {useEffectWithDeps} from 'src/hooks/useEffectWithDeps';
import {getUsersCRMIDsByEmails} from 'src/openapi/admin/apis/GetUsersCrmIdsApi';
import {CrmIdType} from 'src/openapi/admin/models/CrmIdType';
import {useAppDispatch} from 'src/store/hooks';
import {isDeepEqual} from 'src/utils/isDeepEqual';

import CheckBox from '@pb/kit-component-checkbox';
import {EllipsisTextHint} from '@pb/kit-component-ellipsis-text-hint';
import {Group, Item} from '@pb/kit-component-group';
import {Icon, Icons} from '@pb/kit-component-icon';
import Loading, {Types} from '@pb/kit-component-loading';
import {Message} from '@pb/kit-component-message';
import {PaginationStripe} from '@pb/kit-component-pagination';
import {Tooltip} from '@pb/kit-component-popups';
import {Cell, Row} from '@pb/kit-component-table';

import {PB_HEADERS} from '../../constants';
import {parseCSVToUserCRMIDType} from '../../utils/parseCSVFileContent';

import {uploadFileBlockEmailCell, uploadFileBlockIdCell} from './upload-file.module.styl';

import type {UserCRMIDType} from 'src/modules/bulk-update/crm-ids-update/typedef';
import type {Error} from 'src/openapi/admin/models/Error';
import type {Pagination} from 'src/openapi/admin/models/Pagination';
import type {UserCrmIds} from 'src/openapi/admin/models/UserCrmIds';


const getCRMId = (selectedType: CrmIdType, existedData?: UserCrmIds) => {
    switch (selectedType) {
        case CrmIdType.SFDC:
            return existedData?.SFDCId || '';
        case CrmIdType.MS_DYNAMICS:
            return existedData?.MSDynId || '';
        case CrmIdType.EXTERNAL_CRM:
            return existedData?.externalCrmId || '';
        case CrmIdType.DEAL_CLOUD:
            return existedData?.dealCloudId || '';
        case CrmIdType.HUBSPOT:
            return existedData?.hubSpotId?.toString() || '';
        default:
            return '';
    }
};

type Props = {
    addedFile: File | null,
    selectedUser: Array<string>,
    setSelectedUser: (userEmails: Array<string>) => void,
    selectedCRMType: string,
    usersCRMIDs: Array<UserCrmIds> | null,
    setUserCRMIDs: (userCRMIDs: Array<UserCrmIds>) => void,
    templateData: Array<UserCRMIDType>,
    setTemplateData: (params: Array<UserCRMIDType>) => void,
};

const CHUNK_SIZE = 5000;

export const PreviewFileContentBlock = (props: Props) => {
    const appDispatch = useAppDispatch();

    const {
        addedFile,
        selectedUser,
        setSelectedUser,
        templateData,
        setTemplateData,
        selectedCRMType,
        usersCRMIDs,
        setUserCRMIDs: setUpdatesUserCRMIDs,
    } = props;
    const [pagination, setPagination] = useState<Pagination>({currentPage: 1, pageSize: 10});
    const {currentPage, pageSize} = pagination;

    const onPaginationChange = useCallback((changedPagination: Pagination) => {
        setPagination(changedPagination);
    }, []);

    const [error, setError] = useState<Error | null>(null);

    useEffect(() => {
        if (addedFile && selectedCRMType) {
            parseCSVToUserCRMIDType(addedFile).then(userCrmIdTypes => {
                setTemplateData(userCrmIdTypes);
            });
        }
    }, [addedFile, selectedCRMType, setTemplateData]);


    useEffectWithDeps((prev, current) => {
        if (templateData.length && !isDeepEqual({prev, current})) {
            const chunks: UserCRMIDType[][] = [];
            for (let i = 0; i < templateData.length; i += CHUNK_SIZE) {
                chunks.push(templateData.slice(i, i + CHUNK_SIZE));
            }
            let loadedData: UserCrmIds[] = [];

            const loadChunks = async () => {
                for (const chunk of chunks) {
                    const chunkLoadResult = await appDispatch(getUsersCRMIDsByEmails({
                        getUserCrmIdsRequest: {userEmails: chunk.map(e => e.userEmail)},
                    }));

                    if (chunkLoadResult.payload.success) {
                        loadedData = [...loadedData, ...chunkLoadResult.payload.data];
                    } else {
                        setError(chunkLoadResult.payload.data);
                        break;
                    }
                }

                setUpdatesUserCRMIDs(loadedData);
            };

            loadChunks();
        }
    }, templateData);

    const onCheckBoxChange = useCallback((e: {
        event: React.SyntheticEvent<HTMLInputElement, Event>,
        name: string,
    }) => {
        const clickedUser = e.name;
        const isChecked = selectedUser.includes(clickedUser);

        if (isChecked) {
            setSelectedUser(selectedUser.filter(email => email !== clickedUser));
        } else {
            setSelectedUser([...selectedUser, clickedUser]);
        }
    }, [selectedUser, setSelectedUser]);

    const onSelectAll = useCallback(() => {
        if (selectedUser.length && selectedUser.length === usersCRMIDs?.length) {
            setSelectedUser([]);
        } else {
            setSelectedUser(usersCRMIDs?.map(e => e.userEmail) || []);
        }
    }, [selectedUser, setSelectedUser, usersCRMIDs]);

    const tableHeader = useMemo(() => {
        return ([
            {
                cell: (
                    <Group alignItems="center">
                        <CheckBox
                            checked={!!selectedUser.length && selectedUser.length === usersCRMIDs?.length}
                            indeterminate={
                                !!selectedUser.length && selectedUser.length !== usersCRMIDs?.length
                            }
                            name="select-all"
                            disabled={!templateData.length || !usersCRMIDs?.length || !usersCRMIDs?.length}
                            onCheckBoxChange={onSelectAll}
                        />
                        <Item style={{width: 350}}>
                            <Group alignItems="center">
                                <span>Email ({templateData.length})</span>
                                {!!templateData.length && !usersCRMIDs && (
                                    <span title="Matching user emails">
                                        <Loading type={Types.ICON}/>
                                    </span>
                                )}
                                {(!templateData.length || (usersCRMIDs && !usersCRMIDs.length)) ? (
                                    <span title="Not found matched emails">
                                        <Icon type={Icons.ERROR}/>
                                    </span>
                                ) : null}
                            </Group>
                        </Item>
                    </Group>
                ),
                props: {width: 400},
            },
            {cell: <span>Old {PB_HEADERS[selectedCRMType]}</span>},
            {cell: <span>New {PB_HEADERS[selectedCRMType]}</span>},
        ]);
    }, [onSelectAll, selectedCRMType, selectedUser, templateData, usersCRMIDs]);

    const paginatedResults = useMemo(() => {
        return templateData
            .slice((currentPage - 1) * pageSize, currentPage * pageSize)
            .map((row, index) => {
                const existedData = usersCRMIDs?.find(e => e.userEmail === row.userEmail);

                const isRealUser = !!existedData?.userId;
                const crmId = getCRMId(selectedCRMType as CrmIdType, existedData);
                return (
                    <Row key={row.userEmail + index}>
                        <Cell className={isRealUser ? uploadFileBlockEmailCell : 'disabled'}>
                            <Group alignItems="center">
                                <CheckBox
                                    checked={
                                        !!existedData?.userEmail &&
                                        selectedUser.includes(existedData?.userEmail)
                                    }
                                    name={`${existedData?.userEmail}`}
                                    disabled={!existedData}
                                    onCheckBoxChange={onCheckBoxChange}
                                />
                                <Item style={{width: 350}}>
                                    {existedData ? (
                                        <Group alignItems="center">
                                            {crmId && !row.crmId ? (
                                                <Tooltip
                                                    anchor={({anchorRef, getAnchorProps}) => (
                                                        <Icon
                                                            type={Icons.ERROR}
                                                            {...getAnchorProps()}
                                                            ref={anchorRef}
                                                        />
                                                    )}
                                                    color="light"
                                                    placement="top-start">
                                                    CRM ID will be removed
                                                </Tooltip>
                                            ) : null}
                                            <UserLink
                                                place="Update User CRM IDs Result Modal"
                                                userId={existedData.userId}
                                                caption={existedData.userEmail}
                                            />
                                        </Group>
                                    ) : (
                                        <EllipsisTextHint title={row.userEmail}>
                                            {row.userEmail}
                                        </EllipsisTextHint>
                                    )}
                                </Item>
                            </Group>
                        </Cell>
                        <Cell className={isRealUser ? uploadFileBlockIdCell : 'disabled'}>
                            <EllipsisTextHint title={crmId}>
                                {crmId}
                            </EllipsisTextHint>
                        </Cell>
                        <Cell className={isRealUser ? uploadFileBlockIdCell : 'disabled'}>
                            <EllipsisTextHint title={row.crmId}>
                                {row.crmId}
                            </EllipsisTextHint>
                        </Cell>
                    </Row>
                );
            });
    }, [currentPage, onCheckBoxChange, pageSize, selectedCRMType, selectedUser, templateData, usersCRMIDs]);

    if (error) {
        return (
            <Message
                color="error"
                onClose={() => setError(null)}>
                {error?.message}
            </Message>
        );
    }
    return (
        <Group direction="v">
            <ScrollableTable
                maxBodyHeight={480}
                head={tableHeader}
                body={paginatedResults}
            />
            <PaginationStripe
                pageSize={pageSize}
                totalCount={templateData.length}
                currentPage={currentPage}
                onPaginationChange={onPaginationChange}
                goToPage={true}
                sizeSwitcherPosition="LEFT"
                sizeSwitcherType="ONLY_PAGE_SIZE"
                sizes={[5, 10]}
            />
        </Group>
    );
};
